/**
 * @fileoverview Global `document` keydown handling for KorvoChat
 *
 * @purpose Keeps KorvoChat.tsx smaller for complexity/CRAP tooling by isolating
 *          reset-dialog focus traps, edit-mode Escape, and composer shortcuts.
 *
 * @usage KorvoChat registers the returned listener in a `useEffect` on `document`.
 *
 * @ownership KorvoChat component infrastructure
 *
 * @dependencies None (DOM + KeyboardEvent only)
 *
 * @restrictions DO NOT read React state here — pass all behavior via `KorvoChatDocumentKeydownContext`.
 */

import type { RefObject } from 'react'

export type KorvoChatDocumentKeydownContext = {
  showResetConfirm: boolean
  /** US-AM-34: Escape closes the admin debug panel before other handlers consume the key. */
  debugPanelOpen: boolean
  closeDebugPanel: () => void
  editingMessageId: string | null
  debugFeatureEnabled: boolean
  resetConversationFeatureEnabled: boolean
  cancelResetConversation: () => void
  confirmResetConversation: () => void
  clearEditingMessage: () => void
  sendMessage: () => void
  composerInputRef: RefObject<HTMLTextAreaElement | null>
  toggleDebug: () => void
  openResetConversation: () => void
}

function focusDialogButtonByIndex(buttons: NodeListOf<Element>, index: number): void {
  const el = buttons[index] as HTMLElement | undefined
  el?.focus()
}

function handleResetConfirmKeys(e: KeyboardEvent, ctx: KorvoChatDocumentKeydownContext): void {
  if (e.key === 'Escape') {
    e.preventDefault()
    ctx.cancelResetConversation()
    return
  }
  if (e.key === 'Tab') {
    const buttons = document.querySelectorAll('[role="dialog"] button')
    if (buttons.length === 0) {
      return
    }
    e.preventDefault()
    const currentIndex = Array.from(buttons).indexOf(document.activeElement as Element)
    const nextIndex = e.shiftKey
      ? (currentIndex - 1 + buttons.length) % buttons.length
      : (currentIndex + 1) % buttons.length
    focusDialogButtonByIndex(buttons, nextIndex)
    return
  }
  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
    const buttons = document.querySelectorAll('[role="dialog"] button')
    if (buttons.length === 0) {
      return
    }
    e.preventDefault()
    const currentIndex = Array.from(buttons).indexOf(document.activeElement as Element)
    const nextIndex =
      e.key === 'ArrowLeft'
        ? (currentIndex - 1 + buttons.length) % buttons.length
        : (currentIndex + 1) % buttons.length
    focusDialogButtonByIndex(buttons, nextIndex)
    return
  }
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    const activeText = document.activeElement?.textContent
    if (activeText === 'Cancel') {
      ctx.cancelResetConversation()
    } else if (activeText === 'Reset') {
      ctx.confirmResetConversation()
    }
  }
}

function handleEditingKeys(e: KeyboardEvent, ctx: KorvoChatDocumentKeydownContext): boolean {
  if (e.key !== 'Escape') {
    return false
  }
  ctx.clearEditingMessage()
  return true
}

function handleGlobalShortcuts(e: KeyboardEvent, ctx: KorvoChatDocumentKeydownContext): void {
  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
    e.preventDefault()
    const trimmed = ctx.composerInputRef.current?.value.trim()
    if (trimmed) {
      ctx.sendMessage()
    }
    return
  }
  if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
    e.preventDefault()
    ctx.composerInputRef.current?.focus()
    return
  }
  if ((e.ctrlKey || e.metaKey) && e.key === '/') {
    e.preventDefault()
    if (ctx.debugFeatureEnabled) {
      ctx.toggleDebug()
    }
    return
  }
  if ((e.ctrlKey || e.metaKey) && e.key === 'r') {
    e.preventDefault()
    if (ctx.resetConversationFeatureEnabled) {
      ctx.openResetConversation()
    }
  }
}

/** Listener for `document.addEventListener('keydown', …)`. */
export function createKorvoChatDocumentKeydownHandler(
  ctx: KorvoChatDocumentKeydownContext,
): (e: KeyboardEvent) => void {
  return (e: KeyboardEvent) => {
    if (ctx.showResetConfirm) {
      handleResetConfirmKeys(e, ctx)
      return
    }
    if (ctx.debugPanelOpen && e.key === 'Escape') {
      e.preventDefault()
      ctx.closeDebugPanel()
      return
    }
    if (ctx.editingMessageId) {
      handleEditingKeys(e, ctx)
      return
    }
    handleGlobalShortcuts(e, ctx)
  }
}
